'use client';

import React, { useState } from 'react';
import { Save, Settings, AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import { DEFAULT_SETTINGS, EggProductionSettings } from '../../components/eggProductionData';
import { useRole } from '@/lib/useRole';
import Link from 'next/link';

export default function EggSettingsContent() {
  const [settings, setSettings] = useState<EggProductionSettings>(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const { can } = useRole();

  if (!can('egg_settings.manage')) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
          <Shield size={28} className="text-red-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-700">Access Denied</h2>
        <p className="text-sm text-gray-400">You do not have permission to manage egg production settings.</p>
        <Link href="/egg-production" className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">
          Back to Overview
        </Link>
      </div>
    );
  }

  function set<K extends keyof EggProductionSettings>(key: K, value: EggProductionSettings[K]) {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Egg Production Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Configure thresholds, defaults, and integration settings for the egg production module.</p>
        </div>
        <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm">
          <Save size={15} />Save Settings
        </button>
      </div>

      {saved && (
        <div className="mb-4 flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-xl">
          <CheckCircle size={16} />Settings saved successfully.
        </div>
      )}

      <div className="space-y-6">
        {/* Production Defaults */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Settings size={16} className="text-green-600" />Production Defaults
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Default Tray Size (eggs per tray)</label>
              <select value={settings.defaultTraySize} onChange={e => set('defaultTraySize', Number(e.target.value))} className={inputCls}>
                <option value={6}>6 eggs</option>
                <option value={12}>12 eggs</option>
                <option value={30}>30 eggs</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Used for automatic tray calculations.</p>
            </div>
          </div>
        </div>

        {/* Alert Thresholds */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" />Alert Thresholds
          </h2>
          <p className="text-xs text-gray-400 mb-4">Configure when alerts are triggered for production issues.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: 'productionTargetThreshold' as keyof EggProductionSettings, label: 'Production Target Threshold (%)', desc: 'Alert when HDP% falls below target by this amount.' },
              { key: 'crackRateThreshold' as keyof EggProductionSettings, label: 'Crack Rate Threshold (%)', desc: 'Alert when crack rate exceeds this percentage.' },
              { key: 'rejectRateThreshold' as keyof EggProductionSettings, label: 'Rejection Rate Threshold (%)', desc: 'Alert when rejection rate exceeds this percentage.' },
              { key: 'dirtyEggThreshold' as keyof EggProductionSettings, label: 'Dirty Egg Threshold (%)', desc: 'Alert when dirty egg rate exceeds this percentage.' },
              { key: 'productionAlertThreshold' as keyof EggProductionSettings, label: 'Production Drop Alert (%)', desc: 'Alert when production drops by this % vs previous day.' },
            ].map(({ key, label, desc }) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                <input
                  type="number"
                  value={settings[key] as number}
                  onChange={e => set(key, Number(e.target.value))}
                  className={inputCls}
                  min="0"
                  max="100"
                  step="0.5"
                />
                <p className="text-xs text-gray-400 mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Scoring */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4">Performance Scoring Thresholds</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: 'Excellent', desc: 'HDP% ≥ Target', color: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700' },
              { label: 'Good', desc: 'Within 5% below target', color: 'bg-blue-50 border-blue-200', badge: 'bg-blue-100 text-blue-700' },
              { label: 'Below Target', desc: '5%–10% below target', color: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700' },
              { label: 'Critical', desc: 'More than 10% below target', color: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700' },
            ].map(s => (
              <div key={s.label} className={`flex items-center gap-3 p-3 rounded-xl border ${s.color}`}>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.badge}`}>{s.label}</span>
                <span className="text-xs text-gray-600">{s.desc}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">Thresholds are based on the Production Target Threshold setting above.</p>
        </div>

        {/* Integration Settings */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4">Integration & Workflow Settings</h2>
          <div className="space-y-4">
            {[
              { key: 'autoStockCreation' as keyof EggProductionSettings, label: 'Automatic Stock Creation', desc: 'Automatically create inventory entries when production records are approved.' },
              { key: 'approvalRequired' as keyof EggProductionSettings, label: 'Approval Required', desc: 'Require manager approval before production records affect inventory.' },
              { key: 'inventoryIntegration' as keyof EggProductionSettings, label: 'Inventory Integration', desc: 'Connect egg production to the inventory module for stock tracking.' },
              { key: 'salesIntegration' as keyof EggProductionSettings, label: 'Sales Integration', desc: 'Allow egg inventory to be sold through the Sales Management module.' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-start justify-between gap-4 py-3 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                </div>
                <button
                  onClick={() => set(key, !settings[key] as EggProductionSettings[typeof key])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${settings[key] ? 'bg-green-600' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings[key] ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
